/**
 * @fileoverview ヒヤリハット報告 一覧・分析・対応
 * @description 乗組員からのヒヤリハット報告を、KPI・カード/テーブル一覧・詳細ドロワーで管理し、
 *              分析タブ（月別トレンド・カテゴリ別・船舶別・状態構成）で傾向を可視化する。
 *              全データを取得しクライアント側で絞り込むため、KPI件数と一覧件数は常に一致する。
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { get } from '../services/api';
import {
  Camera, AlertTriangle, ShieldAlert, ListChecks, CheckCircle2, CalendarClock,
  LayoutGrid, List as ListIcon, BarChart2, ClipboardList, User, Ship, Tag, Clock,
} from 'lucide-react';
import { DateRangeFilter, inDateRange } from '../components/DateRangeFilter';
import MasterLinkButton from '../components/MasterLinkButton';
import { useSort, usePager, SortableTh } from '../components/TableTools';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import IncidentDrawer, {
  type Incident, SEVERITY_META, STATUS_META, Pill, fmtDateTime, daysSince,
} from '../components/IncidentDrawer';

interface CategoryMaster { id: string; name: string; color: string; is_active?: number }

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1 };
const STATUS_RANK: Record<string, number> = { open: 0, reviewing: 1, closed: 2 };
const CHART_BLUE = '#0ea5e9';

// ── KPIカード（人材/船舶ページと共通の見た目）───────────────────────────────
const StatCard: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string;
  color: string; bg: string; active?: boolean; onClick?: () => void;
}> = ({ icon, label, value, sub, color, bg, active, onClick }) => (
  <div className="stat-card" onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 14, cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid var(--border)',
      boxShadow: active ? `0 4px 14px ${color}33` : undefined,
      transition: 'box-shadow .12s, border-color .12s, transform .12s',
    }}
    onMouseEnter={e => { if (onClick && !active) e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
    title={onClick ? 'クリックで絞り込み' : undefined}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{label}{sub && <span style={{ color: '#94a3b8', fontWeight: 500 }}> ・{sub}</span>}</div>
    </div>
  </div>
);

const ChartCard: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="card" style={{ margin: 0 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{title}</span>
      {hint && <span style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const chartTooltip = (
  <Tooltip cursor={{ fill: 'rgba(148,163,184,0.12)' }}
    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }} />
);

type PageTab = 'list' | 'analytics';

const IncidentsPage: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [cats, setCats] = useState<CategoryMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<PageTab>('list');
  const [view, setView] = useState<'cards' | 'table'>('cards');

  const [fStatus, setFStatus] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fSeverity, setFSeverity] = useState('');
  const [fVessel, setFVessel] = useState('');
  const [fName, setFName] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [selected, setSelected] = useState<Incident | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get<any>('/incidents', {});
      setIncidents((res.data ?? []) as Incident[]);
    } catch (e) { console.error('[IncidentsPage]', e); }
    setLoading(false);
  }, []);
  const loadCats = useCallback(async () => {
    try { const res = await get<any>('/incidents/categories', {}); setCats((res.data ?? []) as CategoryMaster[]); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); loadCats(); }, [load, loadCats]);

  const catColor = useCallback((name?: string | null) => cats.find(c => c.name === name)?.color, [cats]);
  const categoryOptions = useMemo(() => {
    const fromMaster = cats.map(c => c.name);
    const fromData = Array.from(new Set(incidents.map(i => i.category).filter((c): c is string => !!c && c !== '未分類')));
    return Array.from(new Set([...fromMaster, ...fromData]));
  }, [cats, incidents]);
  const vesselOptions = useMemo(
    () => Array.from(new Set(incidents.map(i => i.vessel_name).filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b, 'ja')),
    [incidents]);

  // ── 絞り込み（すべてクライアント側）────────────────────────────────────────
  const filtered = useMemo(() => incidents.filter(i => {
    if (fStatus && i.status !== fStatus) return false;
    if (fSeverity && i.severity !== fSeverity) return false;
    if (fCategory && i.category !== fCategory) return false;
    if (fVessel && i.vessel_name !== fVessel) return false;
    if (fName && !(i.reporter_name ?? '').includes(fName.trim())) return false;
    if (!inDateRange(i.occurred_at ?? '', fFrom, fTo)) return false;
    return true;
  }), [incidents, fStatus, fSeverity, fCategory, fVessel, fName, fFrom, fTo]);

  const sort = useSort<Incident>(filtered, {
    occurred_at: i => i.occurred_at, reporter_name: i => i.reporter_name, vessel_name: i => i.vessel_name,
    category: i => i.category, title: i => i.title,
    severity: i => SEVERITY_RANK[i.severity] ?? 99, status: i => STATUS_RANK[i.status] ?? 99,
  }, 'occurred_at', 'desc');
  const pager = usePager(sort.sorted, { noun: '件' });

  // ── KPI（全件から算出：カード=クリックで絞り込み）──────────────────────────
  const todayStr = new Date().toISOString().slice(0, 10);
  const d30Str = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const isRecent = fFrom === d30Str && fTo === todayStr;
  const kpi = useMemo(() => {
    const total = incidents.length;
    const critical = incidents.filter(i => i.severity === 'critical').length;
    const open = incidents.filter(i => i.status === 'open').length;
    const reviewing = incidents.filter(i => i.status === 'reviewing').length;
    const closed = incidents.filter(i => i.status === 'closed').length;
    const last30 = incidents.filter(i => (i.occurred_at ?? '') >= new Date(Date.now() - 30 * 86400000).toISOString()).length;
    return { total, critical, open, reviewing, closed, last30 };
  }, [incidents]);

  const clearAll = () => { setFStatus(''); setFSeverity(''); setFCategory(''); setFVessel(''); setFName(''); setFFrom(''); setFTo(''); };
  const toggleStatus = (s: string) => { setFStatus(prev => prev === s ? '' : s); };

  const hasFilter = !!(fStatus || fSeverity || fCategory || fVessel || fName || fFrom || fTo);

  // ── 分析 ────────────────────────────────────────────────────────────────────
  const analytics = useMemo(() => {
    // 月別（直近12ヶ月）
    const now = new Date();
    const months: { label: string; y: number; m: number; critical: number; warning: number }[] = [];
    for (let k = 11; k >= 0; k--) {
      const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
      months.push({ label: `${d.getMonth() + 1}月`, y: d.getFullYear(), m: d.getMonth(), critical: 0, warning: 0 });
    }
    for (const i of incidents) {
      const d = new Date(i.occurred_at); if (isNaN(d.getTime())) continue;
      const b = months.find(x => x.y === d.getFullYear() && x.m === d.getMonth());
      if (b) { if (i.severity === 'critical') b.critical++; else b.warning++; }
    }
    // カテゴリ別
    const catMap = new Map<string, number>();
    for (const i of incidents) { const c = i.category || '未分類'; catMap.set(c, (catMap.get(c) ?? 0) + 1); }
    const byCategory = Array.from(catMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    // 船舶別（上位8）
    const vMap = new Map<string, number>();
    for (const i of incidents) { const v = i.vessel_name || '未設定'; vMap.set(v, (vMap.get(v) ?? 0) + 1); }
    const byVessel = Array.from(vMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8);
    // 状態構成
    const byStatus = [
      { key: 'open', name: '未対応', count: kpi.open, color: STATUS_META.open!.fg },
      { key: 'reviewing', name: '対応中', count: kpi.reviewing, color: STATUS_META.reviewing!.fg },
      { key: 'closed', name: '完了', count: kpi.closed, color: STATUS_META.closed!.fg },
    ];
    return { months, byCategory, byVessel, byStatus };
  }, [incidents, kpi]);

  const drillTo = (fn: () => void) => { clearAll(); fn(); setTab('list'); };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">ヒヤリハット報告</h1>
        <div className="flex-row">
          <MasterLinkButton tab="incident-category" title="ヒヤリハットカテゴリのマスタを開きます" />
        </div>
      </div>

      {/* タブ */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e2e8f0', marginBottom: 18 }}>
        {([['list', <ClipboardList key="i" size={15} />, '一覧'], ['analytics', <BarChart2 key="i" size={15} />, '分析']] as [PageTab, React.ReactNode, string][]).map(([k, icon, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? '#0369a1' : '#64748b', borderBottom: tab === k ? '2.5px solid #0369a1' : '2.5px solid transparent', marginBottom: -1 }}>
            {icon}{label}
          </button>
        ))}
      </div>

      {/* KPI（両タブ共通）*/}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: 18 }}>
        <StatCard icon={<ListChecks size={20} />} label="総件数" value={kpi.total} color="#0369a1" bg="#e0f2fe"
          active={!hasFilter} onClick={clearAll} />
        <StatCard icon={<ShieldAlert size={20} />} label="重大" value={kpi.critical} color="#dc2626" bg="#fef2f2"
          active={fSeverity === 'critical'} onClick={() => setFSeverity(p => p === 'critical' ? '' : 'critical')} />
        <StatCard icon={<AlertTriangle size={20} />} label="未対応" value={kpi.open} color="#dc2626" bg="#fef2f2"
          active={fStatus === 'open'} onClick={() => toggleStatus('open')} />
        <StatCard icon={<Clock size={20} />} label="対応中" value={kpi.reviewing} color="#d97706" bg="#fffbeb"
          active={fStatus === 'reviewing'} onClick={() => toggleStatus('reviewing')} />
        <StatCard icon={<CheckCircle2 size={20} />} label="完了" value={kpi.closed} color="#16a34a" bg="#dcfce7"
          active={fStatus === 'closed'} onClick={() => toggleStatus('closed')} />
        <StatCard icon={<CalendarClock size={20} />} label="直近30日" value={kpi.last30} color="#2563eb" bg="#eff6ff"
          active={isRecent} onClick={() => { if (isRecent) { setFFrom(''); setFTo(''); } else { setFFrom(d30Str); setFTo(todayStr); } }} />
      </div>

      {/* ═══ 一覧タブ ═══ */}
      {tab === 'list' && (
        <>
          <div className="flex-row" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div className="form-g" style={{ margin: 0, minWidth: 140 }}>
              <label className="form-l">カテゴリ</label>
              <select className="form-i" value={fCategory} onChange={e => setFCategory(e.target.value)}>
                <option value="">すべて</option>
                {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-g" style={{ margin: 0, minWidth: 150 }}>
              <label className="form-l">船舶</label>
              <select className="form-i" value={fVessel} onChange={e => setFVessel(e.target.value)}>
                <option value="">すべて</option>
                {vesselOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-g" style={{ margin: 0, minWidth: 170 }}>
              <label className="form-l">報告者</label>
              <input className="form-i" type="text" placeholder="名前で検索" value={fName} onChange={e => setFName(e.target.value)} />
            </div>
            <DateRangeFilter from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} />
            {hasFilter && (
              <button onClick={clearAll} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>絞り込み解除</button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{filtered.length}件</span>
              {/* 表示切替 */}
              <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                {([['cards', <LayoutGrid key="i" size={15} />], ['table', <ListIcon key="i" size={15} />]] as ['cards' | 'table', React.ReactNode][]).map(([k, icon]) => (
                  <button key={k} onClick={() => setView(k)} title={k === 'cards' ? 'カード表示' : 'テーブル表示'}
                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', background: view === k ? '#fff' : 'transparent', color: view === k ? '#0369a1' : '#94a3b8', boxShadow: view === k ? '0 1px 3px rgba(15,23,42,0.12)' : 'none' }}>{icon}</button>
                ))}
              </div>
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner" /></div> : (
            <>
              {pager.renderBar('top')}
              {pager.pageRows.length === 0 ? (
                <div className="card empty" style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>該当する報告がありません</div>
              ) : view === 'cards' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                  {pager.pageRows.map(i => {
                    const sev = SEVERITY_META[i.severity];
                    const openDays = ['open', 'reviewing'].includes(i.status) ? daysSince(i.occurred_at) : null;
                    const cc = catColor(i.category);
                    return (
                      <div key={i.id} onClick={() => setSelected(i)}
                        style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid ${sev?.fg ?? '#cbd5e1'}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .12s, transform .12s' }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(15,23,42,0.10)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <Pill meta={sev} />
                          <Pill meta={STATUS_META[i.status]} />
                          {i.category && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: (cc ?? '#64748b') + '18', color: cc ?? '#64748b' }}><Tag size={10} />{i.category}</span>
                          )}
                          {i.has_image && <Camera size={14} color="#2563eb" style={{ marginLeft: 'auto' }} />}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>{i.title}</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{i.description}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, color: '#94a3b8', flexWrap: 'wrap', marginTop: 'auto', paddingTop: 4 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><User size={12} />{i.reporter_name || '-'}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Ship size={12} />{i.vessel_name || '-'}</span>
                          <span style={{ marginLeft: 'auto' }}>{fmtDateTime(i.occurred_at)}</span>
                          {openDays != null && openDays >= 14 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#dc2626', fontWeight: 700 }}><Clock size={11} />{openDays}日未対応</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <SortableTh label="発生日時" k="occurred_at" sort={sort} />
                        <SortableTh label="報告者" k="reporter_name" sort={sort} />
                        <SortableTh label="船舶" k="vessel_name" sort={sort} />
                        <SortableTh label="カテゴリ" k="category" sort={sort} />
                        <SortableTh label="タイトル" k="title" sort={sort} />
                        <SortableTh label="重要度" k="severity" sort={sort} />
                        <SortableTh label="状態" k="status" sort={sort} />
                        <th>写真</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pager.pageRows.map(i => (
                        <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(i)}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(i.occurred_at)}</td>
                          <td>{i.reporter_name || '-'}</td>
                          <td>{i.vessel_name || '-'}</td>
                          <td>{i.category || '-'}</td>
                          <td style={{ fontWeight: 600, maxWidth: 260 }}>{i.title}</td>
                          <td><Pill meta={SEVERITY_META[i.severity]} /></td>
                          <td><Pill meta={STATUS_META[i.status]} /></td>
                          <td>{i.has_image ? <Camera size={16} color="#2563eb" /> : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pager.renderBar('bottom')}
            </>
          )}
        </>
      )}

      {/* ═══ 分析タブ ═══ */}
      {tab === 'analytics' && (
        loading ? <div className="loading"><div className="spinner" /></div> : (
          <>
            <div style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>💡 グラフのバーをクリックすると、該当する報告を一覧で絞り込み表示します</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16 }}>
              {/* 月別発生件数 */}
              <ChartCard title="月別 発生件数（直近12ヶ月）" hint="重大（赤）／注意（橙）の積み上げ">
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={analytics.months} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    {chartTooltip}
                    <Bar dataKey="warning" stackId="a" name="注意" fill="#f59e0b" radius={[0, 0, 0, 0]} maxBarSize={34} />
                    <Bar dataKey="critical" stackId="a" name="重大" fill="#dc2626" radius={[4, 4, 0, 0]} maxBarSize={34} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* カテゴリ別 */}
              <ChartCard title="カテゴリ別 件数" hint="クリックで該当カテゴリを表示">
                <ResponsiveContainer width="100%" height={Math.max(230, analytics.byCategory.length * 34)}>
                  <BarChart data={analytics.byCategory} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                    {chartTooltip}
                    <Bar dataKey="count" name="件数" radius={[0, 5, 5, 0]} maxBarSize={22} cursor="pointer"
                      onClick={(d: any) => { if (d?.name && d.name !== '未分類') drillTo(() => setFCategory(d.name)); }}>
                      {analytics.byCategory.map((d, idx) => <Cell key={idx} fill={catColor(d.name) ?? '#f59e0b'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* 船舶別 */}
              <ChartCard title="船舶別 件数（上位8）" hint="クリックで該当船舶を表示">
                <ResponsiveContainer width="100%" height={Math.max(230, analytics.byVessel.length * 34)}>
                  <BarChart data={analytics.byVessel} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} />
                    {chartTooltip}
                    <Bar dataKey="count" name="件数" radius={[0, 5, 5, 0]} maxBarSize={22} cursor="pointer" fill={CHART_BLUE}
                      onClick={(d: any) => { if (d?.name && d.name !== '未設定') drillTo(() => setFVessel(d.name)); }} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* 状態構成 */}
              <ChartCard title="対応状況の構成" hint="クリックで該当状態を表示">
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={analytics.byStatus} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    {chartTooltip}
                    <Bar dataKey="count" name="件数" radius={[5, 5, 0, 0]} maxBarSize={64} cursor="pointer"
                      onClick={(d: any) => { if (d?.key) drillTo(() => setFStatus(d.key)); }}>
                      {analytics.byStatus.map((d, idx) => <Cell key={idx} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </>
        )
      )}

      {selected && (
        <IncidentDrawer
          incident={selected}
          categoryColor={catColor(selected.category)}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); }}
          onDeleted={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
};

export default IncidentsPage;
